import BarChart from '@mui/icons-material/BarChart'
import StackedLineChart from '@mui/icons-material/StackedLineChart'
import { IconButton, Stack } from '@mui/material'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useGetMetricReport } from '@/service-library/hooks/metrics'
import { ByTimeMetricReport } from '@/types/workflow-metric-reports'
import ByTimeAndStateMetricBarChart from '../ByTimeAndStateMetricBarChart'
import ByTimeAndStateMetricLineChart from '../ByTimeAndStateMetricLineChart'
import ChartCard from '../ChartCard'
import MetricsStatesFilter, {
  useWorkflowSelectedStateIds,
} from '../MetricsStatesFilter'
import { DateRange } from '../ProjectMetricsDashboard'
import { CommonQueryOptions } from '../types'

type IdleDocumentsCountCardProps = {
  commonQueryOptions: CommonQueryOptions & {
    filters: Required<CommonQueryOptions['filters']>
  }
  selectedDateRange: DateRange
  height?: number
}

export default function IdleDocumentsCountCard({
  commonQueryOptions,
  selectedDateRange,
  height,
}: IdleDocumentsCountCardProps) {
  const [variant, setVariant] = useLocalStorage<'bar' | 'line'>(
    'project-metrics-dashboard-idle-documents-card-variant',
    'bar',
  )

  const { report } = useGetMetricReport<ByTimeMetricReport>({
    ...commonQueryOptions,
    reportKey: 'idle-count-eod',
  })

  const {
    allStateIds,
    filteredStateIds,
    selectedStateIds,
    setSelectedStateIds,
  } = useWorkflowSelectedStateIds({
    key: 'idle-count-eod',
    report,
  })

  return (
    <ChartCard
      heading="Idle Documents"
      headingTooltip="The number of documents that were idle in each workflow state at end of day."
      controls={
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={() =>
              setVariant((prev) => (prev === 'bar' ? 'line' : 'bar'))
            }
          >
            {variant === 'bar' ? (
              <StackedLineChart fontSize="small" />
            ) : (
              <BarChart fontSize="small" />
            )}
          </IconButton>
          <MetricsStatesFilter
            allStateIds={allStateIds}
            selectedStateIds={selectedStateIds}
            setSelectedStateIds={setSelectedStateIds}
          />
        </Stack>
      }
    >
      {variant === 'bar' ? (
        <ByTimeAndStateMetricBarChart
          report={report}
          filteredStateIds={filteredStateIds}
          groupBy={commonQueryOptions.filters.grouped_by}
          selectedDateRange={selectedDateRange}
          height={height}
        />
      ) : (
        <ByTimeAndStateMetricLineChart
          type="count"
          report={report}
          filteredStateIds={filteredStateIds}
          groupBy={commonQueryOptions.filters.grouped_by}
          selectedDateRange={selectedDateRange}
          height={height}
        />
      )}
    </ChartCard>
  )
}
